import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui";
import {
  ArrowLeft,
  Star,
  CheckCircle2,
  Loader2,
  AlertCircle,
  TrendingUp,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { getHeroClass } from "../../lib/design-system";
import { getAssetById, Asset } from "../../services/asset.api";
import { calculateHealthScore } from "../../utils/healthScore";

// ── Types ────────────────────────────────────────────────────────────────

export interface NewModel {
  id: string;
  brand: string;
  name: string;
  type: string;
  price: number;
  discountPrice: number;
  discount: number;
  rating: number;
  badges: string[];
  features: string[];
  monthlyPayment: number;
  imageUrl?: string;
  // Additional fields for compatibility
  modelNumber?: string;
  category?: string;
  warrantyYears?: number;
  rebateEligible?: boolean;
  rebateAmount?: number;
  energyStarCertified?: boolean;
  annualEnergyCostEst?: number;
  reviewCount?: number;
  tag?: string;
  tier?: string;
}

type SortKey = "price-asc" | "price-desc" | "rating" | "energy";

// ── Model Catalog ────────────────────────────────────────────────────────

// Product catalog matching mobile app exactly
const MODEL_CATALOG: Record<string, NewModel[]> = {
  refrigerator: [
    {
      id: 'p1',
      brand: 'BOSCH',
      name: 'Bosch French Door Refrigerator',
      type: 'Refrigerator',
      price: 3299,
      discountPrice: 3074,
      discount: 7,
      rating: 4.7,
      badges: ['Top Rated', 'Save 7%'],
      features: [
        'VitaFresh Pro technology',
        'MultiAirflow system',
        'NoFrost technology',
      ],
      monthlyPayment: 256,
    },
    {
      id: 'p2',
      brand: 'SAMSUNG',
      name: 'Samsung 4-Door Flex Refrigerator',
      type: 'Refrigerator',
      price: 2499,
      discountPrice: 2274,
      discount: 8,
      rating: 4.6,
      badges: ['Best Seller', 'Save 8%'],
      features: [
        'FlexZone™ customizable temperature drawer',
        'Twin Cooling Plus™ technology',
        'Ice & Water dispenser',
      ],
      monthlyPayment: 190,
    },
    {
      id: 'p3',
      brand: 'LG',
      name: 'LG InstaView Door-in-Door Refrigerator',
      type: 'Refrigerator',
      price: 2194,
      discountPrice: 1974,
      discount: 10,
      rating: 4.5,
      badges: ['Recommended', 'Save 10%'],
      features: [
        'InstaView™ Door-in-Door®',
        'Craft Ice™ maker',
        'Smart ThinQ® technology',
      ],
      monthlyPayment: 165,
    },
    {
      id: 'p4',
      brand: 'GE',
      name: 'GE Profile French Door Refrigerator',
      type: 'Refrigerator',
      price: 2194,
      discountPrice: 1974,
      discount: 10,
      rating: 4.5,
      badges: ['New Arrival', 'Save 10%'],
      features: [
        'Hands-Free AutoFill',
        'TwinChill™ evaporators',
        'WiFi Connect',
      ],
      monthlyPayment: 165,
    },
    {
      id: 'p5',
      brand: 'Whirlpool',
      name: 'Whirlpool French Door Refrigerator',
      type: 'Refrigerator',
      price: 1860,
      discountPrice: 1674,
      discount: 10,
      rating: 4.4,
      badges: ['Save 10%'],
      features: [
        'Adaptive defrost',
        'Accu-Chill™ temperature system',
        'LED lighting',
      ],
      monthlyPayment: 140,
    },
    {
      id: 'p6',
      brand: 'Frigidaire',
      name: 'Frigidaire Gallery French Door Refrigerator',
      type: 'Refrigerator',
      price: 1860,
      discountPrice: 1674,
      discount: 10,
      rating: 4.4,
      badges: ['Save 10%'],
      features: [
        'Smudge-Proof stainless steel',
        'PureAir Ultra® II filters',
        'Store-More™ shelves',
      ],
      monthlyPayment: 140,
    },
  ],
  microwave: [
    {
      id: 'm1',
      brand: 'Panasonic',
      name: 'Panasonic Countertop Microwave (1.2 cu ft)',
      type: 'Microwave',
      price: 249,
      discountPrice: 219,
      discount: 12,
      rating: 4.5,
      badges: ['Best Seller', 'Save 12%'],
      features: ['Sensor Cook', 'Inverter Technology', 'Quick 30 sec'],
      monthlyPayment: 19,
    },
    {
      id: 'm2',
      brand: 'Whirlpool',
      name: 'Whirlpool Over-the-Range Microwave (1.7 cu ft)',
      type: 'Microwave',
      price: 329,
      discountPrice: 289,
      discount: 12,
      rating: 4.4,
      badges: ['Top Rated', 'Save 12%'],
      features: ['2-Speed Vent', 'LED Cooktop Light', 'Add 30 Seconds'],
      monthlyPayment: 24,
    },
    {
      id: 'm3',
      brand: 'Samsung',
      name: 'Samsung Ceramic Interior Microwave (1.1 cu ft)',
      type: 'Microwave',
      price: 279,
      discountPrice: 249,
      discount: 11,
      rating: 4.3,
      badges: ['Save 11%'],
      features: ['Ceramic Enamel Interior', 'Eco Mode', 'Child Lock'],
      monthlyPayment: 21,
    },
  ],
  dishwasher: [
    {
      id: 'd1',
      brand: 'BOSCH',
      name: 'Bosch 300 Series Dishwasher',
      type: 'Dishwasher',
      price: 999,
      discountPrice: 899,
      discount: 10,
      rating: 4.7,
      badges: ['Top Rated', 'Save 10%'],
      features: ['Quiet 44 dBA', 'AutoAir™ Dry', '3rd Rack'],
      monthlyPayment: 75,
    },
    {
      id: 'd2',
      brand: 'GE',
      name: 'GE Profile Dishwasher with Microban®',
      type: 'Dishwasher',
      price: 879,
      discountPrice: 799,
      discount: 9,
      rating: 4.5,
      badges: ['Best Seller', 'Save 9%'],
      features: ['Dry Boost™', 'Piranha™ Hard Food', 'Sanitize Option'],
      monthlyPayment: 67,
    },
  ],
  washer: [
    {
      id: 'w1',
      brand: 'LG',
      name: 'LG Front Load Washer (4.5 cu ft)',
      type: 'Washing Machine',
      price: 1099,
      discountPrice: 999,
      discount: 9,
      rating: 4.6,
      badges: ['Best Seller', 'Save 9%'],
      features: ['TurboWash™', 'Steam™', 'SmartThinQ®'],
      monthlyPayment: 84,
    },
    {
      id: 'w2',
      brand: 'Samsung',
      name: 'Samsung Top Load Washer (5.0 cu ft)',
      type: 'Washing Machine',
      price: 899,
      discountPrice: 829,
      discount: 8,
      rating: 4.4,
      badges: ['Save 8%'],
      features: ['Active WaterJet', 'Self Clean', 'Vibration Reduction'],
      monthlyPayment: 69,
    },
  ],
  'air conditioner': [
    {
      id: 'a1',
      brand: 'Daikin',
      name: 'Daikin 12,000 BTU Mini Split AC',
      type: 'Air Conditioner',
      price: 1899,
      discountPrice: 1699,
      discount: 11,
      rating: 4.6,
      badges: ['Top Rated', 'Save 11%'],
      features: ['Inverter Compressor', 'Sleep Mode', 'Wi‑Fi Ready'],
      monthlyPayment: 142,
    },
    {
      id: 'a2',
      brand: 'Carrier',
      name: 'Carrier 10,000 BTU Window AC',
      type: 'Air Conditioner',
      price: 599,
      discountPrice: 529,
      discount: 12,
      rating: 4.3,
      badges: ['Save 12%'],
      features: ['Energy Saver', 'Remote Control', '24‑hr Timer'],
      monthlyPayment: 44,
    },
  ],
  default: [
    {
      id: 'p1',
      brand: 'BOSCH',
      name: 'Bosch French Door Refrigerator',
      type: 'Refrigerator',
      price: 3299,
      discountPrice: 3074,
      discount: 7,
      rating: 4.7,
      badges: ['Top Rated', 'Save 7%'],
      features: [
        'VitaFresh Pro technology',
        'MultiAirflow system',
        'NoFrost technology',
      ],
      monthlyPayment: 256,
    },
  ],
};

function formatCurrency(n: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function estimateTradeIn(asset: Asset): number {
  const purchasePrice = asset.purchasePrice ?? 0;
  if (purchasePrice <= 0) return 0;
  const healthScore = calculateHealthScore(asset);
  const age = getAssetAge(asset);
  const lifespanYears = 12;
  const depreciatedValue = Math.max(
    purchasePrice * 0.1,
    purchasePrice * (1 - age / lifespanYears)
  );
  const conditionFactor = Math.min(1, healthScore / 10);
  return Math.round(depreciatedValue * conditionFactor * 0.35);
}

function getAssetAge(asset: Asset): number {
  const year =
    asset.purchaseYear ||
    (asset.purchasedAt ? new Date(asset.purchasedAt).getFullYear() : null);
  if (!year) return 0;
  return new Date().getFullYear() - year;
}

function normalizeCategory(raw: string): string {
  const s = raw.toLowerCase().trim();
  // Order matters: "dishwasher" contains "washer", so check it first.
  if (s.includes('dishwasher') || s.includes('dish washer')) return 'dishwasher';
  if (s.includes('refrigerator') || s.includes('fridge')) return 'refrigerator';
  if (s.includes('washer') || s.includes('washing') || s.includes('laundry') || s.includes('dryer')) return 'washer';
  if (s.includes('microwave')) return 'microwave';
  if (s.includes('air condition') || s.includes('conditioner') || /\bac\b/.test(s) || s.includes('hvac') || s.includes('mini split')) return 'air conditioner';
  return s;
}

// Local product images copied from the mobile app (public/upgrade-images/).
// Keyed by the exact catalog product ID so web and app show identical imagery.
const PRODUCT_IMAGE_MAP: Record<string, string> = {
  // Refrigerators
  'p1': '/upgrade-images/p1-bosch-fridge.jpg',
  'p2': '/upgrade-images/p2-samsung-fridge.jpg',
  'p3': '/upgrade-images/p3-lg-fridge.jpg',
  'p4': '/upgrade-images/p4-ge-fridge.jpg',
  'p5': '/upgrade-images/p5-whirlpool-fridge.jpg',
  'p6': '/upgrade-images/p6-frigidaire-fridge.jpg',
  // Microwaves (no dedicated image in the app — generic appliance image)
  'm1': '/upgrade-images/microwave-generic.jpg',
  'm2': '/upgrade-images/microwave-generic.jpg',
  'm3': '/upgrade-images/microwave-generic.jpg',
  // Dishwashers
  'd1': '/upgrade-images/d1-bosch-dishwasher.jpg',
  'd2': '/upgrade-images/d2-ge-dishwasher.jpg',
  // Washers
  'w1': '/upgrade-images/w1-lg-washer.jpg',
  'w2': '/upgrade-images/w2-samsung-washer.jpg',
  // Air Conditioners
  'a1': '/upgrade-images/a1-daikin-ac.jpg',
  'a2': '/upgrade-images/a2-carrier-ac.jpg',
};

function getModelImage(productType: string, brand: string, productId?: string): string {
  // Exact product-ID match (matches mobile app catalog)
  if (productId && PRODUCT_IMAGE_MAP[productId]) {
    return PRODUCT_IMAGE_MAP[productId];
  }

  const lowerType = productType.toLowerCase();
  const lowerBrand = brand.toLowerCase();

  // Fallback to brand/category matching
  if (lowerType.includes("refrigerator") || lowerType.includes("fridge")) {
    if (lowerBrand.includes("bosch")) return PRODUCT_IMAGE_MAP['p1'];
    if (lowerBrand.includes("samsung")) return PRODUCT_IMAGE_MAP['p2'];
    if (lowerBrand.includes("lg")) return PRODUCT_IMAGE_MAP['p3'];
    if (lowerBrand.includes("ge")) return PRODUCT_IMAGE_MAP['p4'];
    if (lowerBrand.includes("whirlpool")) return PRODUCT_IMAGE_MAP['p5'];
    if (lowerBrand.includes("frigidaire")) return PRODUCT_IMAGE_MAP['p6'];
    return PRODUCT_IMAGE_MAP['p1'];
  }
  if (lowerType.includes("washer") || lowerType.includes("washing")) {
    if (lowerBrand.includes("samsung")) return PRODUCT_IMAGE_MAP['w2'];
    return PRODUCT_IMAGE_MAP['w1'];
  }
  if (lowerType.includes("dishwasher")) {
    if (lowerBrand.includes("ge")) return PRODUCT_IMAGE_MAP['d2'];
    return PRODUCT_IMAGE_MAP['d1'];
  }
  if (lowerType.includes("microwave")) return PRODUCT_IMAGE_MAP['m1'];
  if (lowerType.includes("air conditioner") || lowerType.includes("ac")) {
    if (lowerBrand.includes("carrier")) return PRODUCT_IMAGE_MAP['a2'];
    return PRODUCT_IMAGE_MAP['a1'];
  }

  // Default fallback — generic appliance
  return PRODUCT_IMAGE_MAP['microwave-generic'] ?? PRODUCT_IMAGE_MAP['p1'];
}

// ── Main Component ───────────────────────────────────────────────────────

export function BrowseModels() {
  const { t } = useTranslation('upgrade');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category')?.toLowerCase();

  const [asset, setAsset] = useState<Asset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tierFilter, setTierFilter] = useState<"all" | "budget" | "mid-range" | "premium">("all");
  const [energyStarOnly, setEnergyStarOnly] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("rating");
  const [priceRange, setPriceRange] = useState<"all" | "under-1000" | "1000-1500" | "1500-2500" | "over-2500">("all");
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [minRating, setMinRating] = useState<number>(0);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getAssetById(id)
      .then((data) => setAsset(data))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : t('browse.loadError')))
      .finally(() => setLoading(false));
  }, [id]);

  const tradeInValue = useMemo(() => (asset ? estimateTradeIn(asset) : 0), [asset]);

  // Resolved category key.
  //  - user picked one in the sidebar    → that key ('all' = show everything)
  //  - else derive from URL param / asset → normalized catalog key
  //  - unknown / generic                 → 'all' (show everything)
  const activeCategory = useMemo(() => {
    if (selectedCategory) return selectedCategory; // includes explicit 'all'
    const raw = categoryParam || asset?.category || asset?.subCategory ||
                asset?.productCategory || asset?.name || '';
    const key = normalizeCategory(raw);
    return MODEL_CATALOG[key] ? key : 'all';
  }, [categoryParam, asset, selectedCategory]);

  // Strict: a specific category shows ONLY that category; 'all' shows everything.
  const allModels = useMemo(() => {
    const allCatalogProducts = Object.entries(MODEL_CATALOG)
      .filter(([key]) => key !== 'default')
      .flatMap(([, products]) => products);
    if (activeCategory === 'all') return allCatalogProducts;
    return MODEL_CATALOG[activeCategory] ?? allCatalogProducts;
  }, [activeCategory]);

  // Format category name for display
  const categoryDisplayName = useMemo(() => {
    const categoryMap: Record<string, string> = {
      refrigerator: 'Refrigerators',
      washer: 'Washing Machines',
      dishwasher: 'Dishwashers',
      microwave: 'Microwaves',
      'air conditioner': 'Air Conditioners',
      all: 'Appliances',
      default: 'Appliances',
    };
    return categoryMap[activeCategory] || activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1) + 's';
  }, [activeCategory]);

  // Get unique brands from all models
  const availableBrands = useMemo(() => {
    return Array.from(new Set(allModels.map(m => m.brand))).sort();
  }, [allModels]);

  const filteredAndSorted = useMemo(() => {
    let list = allModels;
    
    // Price range filter
    if (priceRange !== "all") {
      list = list.filter((m) => {
        switch (priceRange) {
          case "under-1000": return m.price < 1000;
          case "1000-1500": return m.price >= 1000 && m.price < 1500;
          case "1500-2500": return m.price >= 1500 && m.price < 2500;
          case "over-2500": return m.price >= 2500;
          default: return true;
        }
      });
    }
    
    // Brand filter
    if (selectedBrands.length > 0) {
      list = list.filter((m) => selectedBrands.includes(m.brand));
    }
    
    // Rating filter
    if (minRating > 0) {
      list = list.filter((m) => m.rating >= minRating);
    }
    
    // Tier filter
    if (tierFilter !== "all") list = list.filter((m) => m.tier === tierFilter);
    
    // Energy star filter (removed - not in new model)
    // if (energyStarOnly) list = list.filter((m) => m.energyStarCertified);
    
    // Sort
    switch (sortKey) {
      case "price-asc":
        return [...list].sort((a, b) => a.price - b.price);
      case "price-desc":
        return [...list].sort((a, b) => b.price - a.price);
      case "rating":
        return [...list].sort((a, b) => b.rating - a.rating);
      case "energy":
        return [...list].sort((a, b) => (a.annualEnergyCostEst ?? 0) - (b.annualEnergyCostEst ?? 0));
      default:
        return list;
    }
  }, [allModels, sortKey, tierFilter, energyStarOnly, priceRange, selectedBrands, minRating]);

  const toggleBrand = (brand: string) => {
    setSelectedBrands(prev =>
      prev.includes(brand)
        ? prev.filter(b => b !== brand)
        : [...prev, brand]
    );
  };

  const clearFilters = () => {
    setTierFilter("all");
    setEnergyStarOnly(false);
    setPriceRange("all");
    setSelectedBrands([]);
    setMinRating(0);
    setSelectedCategory(null);
  };

  const hasActiveFilters = tierFilter !== "all" || energyStarOnly || priceRange !== "all" ||
                           selectedBrands.length > 0 || minRating > 0 || selectedCategory !== null;

  const CATALOG_CATEGORIES = [
    { key: 'refrigerator', label: 'Refrigerators', icon: '🧊' },
    { key: 'washer', label: 'Washing Machines', icon: '🫧' },
    { key: 'dishwasher', label: 'Dishwashers', icon: '🍽️' },
    { key: 'microwave', label: 'Microwaves', icon: '📡' },
    { key: 'air conditioner', label: 'Air Conditioners', icon: '❄️' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <AlertCircle className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{error || t('browse.assetNotFound')}</p>
        <Button variant="outline" onClick={() => navigate("/assets")}>
          {t('browse.backToAssets')}
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <section className="bg-gradient-to-b from-background to-muted/20 border-b">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate(`/assets/${id}/upgrade`)}
              className="p-2 -ml-2 rounded-lg hover:bg-secondary transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className={getHeroClass('main')}>Browse {categoryDisplayName}</h1>
              <p className="text-muted-foreground">
                {asset ? t('browse.replacing', { name: asset.name }) : t('browse.findYourUpgrade')}
              </p>
            </div>
          </div>

          {/* Trade-in Value Banner */}
          {tradeInValue > 0 && (
            <div className="rounded-lg border bg-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{t('browse.tradeInAvailable')}</p>
                  <p className="text-3xl font-light">{formatCurrency(tradeInValue)}</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('browse.creditAppliedAtCheckout')}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-muted-foreground" />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Filters & Results */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Left Sidebar: Filters */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 rounded-xl border bg-card shadow-sm overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
                <h3 className="text-sm font-semibold">Filters</h3>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>

              <div className="divide-y">
                {/* Category */}
                <div className="px-4 py-4 space-y-2.5">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Category</p>
                  <div className="space-y-1">
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className={cn(
                        "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-all",
                        activeCategory === 'all'
                          ? "bg-primary text-white font-medium shadow-sm"
                          : "hover:bg-secondary text-foreground"
                      )}
                    >
                      <span>🏠</span>
                      <span>All Appliances</span>
                      <span className={cn("ml-auto text-xs", activeCategory === 'all' ? "text-white/70" : "text-muted-foreground")}>
                        {Object.entries(MODEL_CATALOG).filter(([k]) => k !== 'default').flatMap(([, p]) => p).length}
                      </span>
                    </button>
                    {CATALOG_CATEGORIES.map(cat => {
                      const count = MODEL_CATALOG[cat.key]?.length ?? 0;
                      const isActive = activeCategory === cat.key;
                      return (
                        <button
                          key={cat.key}
                          onClick={() => setSelectedCategory(isActive ? 'all' : cat.key)}
                          className={cn(
                            "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-all",
                            isActive
                              ? "bg-primary text-white font-medium shadow-sm"
                              : "hover:bg-secondary text-foreground"
                          )}
                        >
                          <span>{cat.icon}</span>
                          <span className="flex-1">{cat.label}</span>
                          <span className={cn("text-xs", isActive ? "text-white/70" : "text-muted-foreground")}>{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Price Range */}
                <div className="px-4 py-4 space-y-2.5">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Price Range</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { value: "all" as const, label: "All" },
                      { value: "under-1000" as const, label: "< $1K" },
                      { value: "1000-1500" as const, label: "$1K–$1.5K" },
                      { value: "1500-2500" as const, label: "$1.5K–$2.5K" },
                      { value: "over-2500" as const, label: "> $2.5K" },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setPriceRange(option.value)}
                        className={cn(
                          "px-2.5 py-1 rounded-full text-xs font-medium border transition-all",
                          priceRange === option.value
                            ? "bg-primary text-white border-primary shadow-sm"
                            : "border-border hover:border-primary/50 hover:text-primary"
                        )}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Brands */}
                {availableBrands.length > 0 && (
                  <div className="px-4 py-4 space-y-2.5">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Brands</p>
                    <div className="flex flex-wrap gap-1.5">
                      {availableBrands.map((brand) => {
                        const active = selectedBrands.includes(brand);
                        return (
                          <button
                            key={brand}
                            onClick={() => toggleBrand(brand)}
                            className={cn(
                              "px-2.5 py-1 rounded-md text-xs border transition-all",
                              active
                                ? "bg-primary/10 text-primary border-primary/40 font-semibold"
                                : "border-border hover:border-primary/40 hover:text-primary"
                            )}
                          >
                            {brand}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Customer Rating */}
                <div className="px-4 py-4 space-y-2.5">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Customer Rating</p>
                  <div className="space-y-1">
                    {[4, 3, 2, 1].map((rating) => (
                      <button
                        key={rating}
                        onClick={() => setMinRating(minRating === rating ? 0 : rating)}
                        className={cn(
                          "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all",
                          minRating === rating
                            ? "bg-primary/10 text-primary font-medium"
                            : "hover:bg-secondary"
                        )}
                      >
                        <div className="flex items-center gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={cn(
                                "h-3.5 w-3.5",
                                i < rating
                                  ? minRating === rating ? "fill-primary text-primary" : "fill-amber-400 text-amber-400"
                                  : "fill-muted text-muted"
                              )}
                            />
                          ))}
                        </div>
                        <span className="text-xs text-muted-foreground">& Up</span>
                      </button>
                    ))}
                    <button
                      onClick={() => setMinRating(0)}
                      className={cn(
                        "w-full flex items-center px-3 py-2 rounded-lg text-sm transition-all",
                        minRating === 0
                          ? "bg-primary/10 text-primary font-medium"
                          : "hover:bg-secondary text-muted-foreground"
                      )}
                    >
                      All Ratings
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Results */}
          <div className="lg:col-span-3 space-y-6">
            {/* Sort & Results Count */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {filteredAndSorted.length} {filteredAndSorted.length === 1 ? t('browse.result') : t('browse.results')}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{t('browse.sortBy')}</span>
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value as SortKey)}
                  className="px-3 py-1.5 rounded-lg border bg-background text-sm"
                >
                  <option value="rating">{t('browse.sortTopRated')}</option>
                  <option value="price-asc">{t('browse.sortPriceAsc')}</option>
                  <option value="price-desc">{t('browse.sortPriceDesc')}</option>
                  <option value="energy">{t('browse.sortEnergy')}</option>
                </select>
              </div>
            </div>

            {/* Model Grid */}
            {filteredAndSorted.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24">
                <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-normal mb-2">{t('browse.noModelsFound')}</h3>
                <p className="text-sm text-muted-foreground mb-4">{t('browse.tryAdjustingFilters')}</p>
                {hasActiveFilters && (
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    {t('browse.clearAllFilters')}
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredAndSorted.map((model) => (
                  <ModelCard
                    key={model.id}
                    model={model}
                    tradeInValue={tradeInValue}
                    onViewDetails={() => navigate(`/assets/${id}/upgrade/model/${model.id}`)}
                    onBuyNow={() =>
                      navigate(`/assets/${id}/upgrade/checkout`, {
                        state: { model, tradeInValue },
                      })
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

// ── Model Card ───────────────────────────────────────────────────────────

interface ModelCardProps {
  model: NewModel;
  tradeInValue: number;
  onViewDetails: () => void;
  onBuyNow: () => void;
}

function getCategoryPlaceholder(type: string): string {
  const t = type.toLowerCase();
  if (t.includes('refrigerator') || t.includes('fridge')) return '🧊';
  if (t.includes('washer') || t.includes('washing')) return '🫧';
  if (t.includes('dishwasher')) return '🍽️';
  if (t.includes('microwave')) return '📡';
  if (t.includes('air') || t.includes('conditioner')) return '❄️';
  return '🏠';
}

function ModelCard({ model, tradeInValue, onViewDetails, onBuyNow }: ModelCardProps) {
  const { t } = useTranslation('upgrade');
  const [imgError, setImgError] = useState(false);
  const effectivePrice = Math.max(0, model.price - tradeInValue);
  const imageUrl = getModelImage(model.type, model.brand, model.id);

  return (
    <div className="group bg-card border rounded-xl overflow-hidden hover:border-primary/30 transition-all hover:shadow-lg">
      {/* Image */}
      <div className="relative aspect-[4/3] bg-white overflow-hidden flex items-center justify-center p-4">
        {imgError ? (
          <div className="w-full h-full bg-gradient-to-br from-muted to-secondary flex flex-col items-center justify-center gap-2">
            <span className="text-5xl">{getCategoryPlaceholder(model.type)}</span>
            <span className="text-xs text-muted-foreground font-medium">{model.brand}</span>
          </div>
        ) : (
          <img
            src={imageUrl}
            alt={`${model.brand} ${model.name}`}
            onError={() => setImgError(true)}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
          />
        )}
        {/* Badges */}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between">
          <div className="flex flex-col gap-2">
            {model.badges?.[0] && (
              <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-background/90 backdrop-blur text-foreground shadow-sm">
                {model.badges[0]}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 space-y-4">
        {/* Brand & Model */}
        <div>
          <p className="text-xs text-muted-foreground mb-1">{model.brand}</p>
          <h3 className="text-lg font-normal leading-tight mb-1">{model.name}</h3>
          <p className="text-xs text-muted-foreground">{model.modelNumber}</p>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-2">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={cn(
                  "h-3.5 w-3.5",
                  i < Math.floor(model.rating)
                    ? "fill-foreground text-foreground"
                    : "fill-muted text-muted"
                )}
              />
            ))}
          </div>
          <span className="text-sm font-medium">{model.rating}</span>
          <span className="text-xs text-muted-foreground">
            
          </span>
        </div>

        {/* Features */}
        <ul className="space-y-1.5">
          {model.features.slice(0, 3).map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
              <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {/* Divider */}
        <div className="border-t" />

        {/* Pricing */}
        <div className="space-y-2">
          {model.price && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{t('browse.msrp')}</span>
              <span className="text-muted-foreground line-through">
                {formatCurrency(model.price)}
              </span>
            </div>
          )}
          
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-muted-foreground">{t('browse.price')}</span>
            <span className="text-2xl font-light">{formatCurrency(model.discountPrice)}</span>
          </div>
          
          {model.discount && (
            <div className="inline-flex items-center gap-1.5 bg-primary/10 text-primary px-2 py-1 rounded-md">
              <span className="text-xs font-medium">Save {model.discount}%</span>
            </div>
          )}

          {tradeInValue > 0 && (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t('browse.tradeIn')}</span>
                <span className="font-medium">−{formatCurrency(tradeInValue)}</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-sm font-medium">{t('browse.finalPrice')}</span>
                <span className="text-xl font-light">{formatCurrency(effectivePrice)}</span>
              </div>
            </>
          )}
        </div>

        {/* Monthly Payment */}
        {model.monthlyPayment && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>or ${model.monthlyPayment}/mo with financing</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={onViewDetails} className="flex-1">
            {t('browse.details')}
          </Button>
          <Button size="sm" onClick={onBuyNow} className="flex-1">
            {t('browse.buyNow')}
          </Button>
        </div>
      </div>
    </div>
  );
}
